import { usePostHog } from '@/composables/usePostHog'
import { productsRoutes } from '@/features/brandProducts/routes'
import { brandsRoutes } from '@/features/brands/routes'
import { customerPortalRoutes } from '@/features/customerPortal/routes'
import { dtcRoutes } from '@/features/dtc/routes'
import { exportsRoutes } from '@/features/exports/routes'
import { listingLinksRoutes } from '@/features/listingLinks/routes'
import { listingTrackingRoutes } from '@/features/listingTracking/routes'
import { notificationsRoutes } from '@/features/notifications/routes'
import { overviewRoutes } from '@/features/overview/routes'
import { plansRoutes } from '@/features/plans/routes'
import { profileRoutes } from '@/features/profile/routes'
import { usePermissions } from '@/features/shared/composables/usePermissions'
import { userRoutes } from '@/features/users/routes'
import { PermissionType } from '@/features/users/types'
import { authRoutes } from '@/router/auth'
import { businessRoutes } from '@/router/business'
import { campaignRoutes } from '@/router/campaign'
import { entityRoutes } from '@/router/entity'
import { priceAlertRoutes } from '@/router/priceAlert'
import { AccountTypeId } from '@/types/entity'
import { useTitle } from '@vueuse/core'
import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { jumpRoutes } from './jump'

const { posthog } = usePostHog()

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Admin',
    redirect: () => {
      return { name: 'Overview' }
    },
    component: () => import('@/views/Admin.vue'),
    meta: {
      requiresAuth: true,
    },
    children: [
      ...overviewRoutes,
      ...brandsRoutes,
      ...entityRoutes,
      ...businessRoutes,
      ...userRoutes,
      ...campaignRoutes,
      ...profileRoutes,
      ...productsRoutes,
      ...priceAlertRoutes,
      ...customerPortalRoutes,
      ...dtcRoutes,
      ...plansRoutes,
      ...exportsRoutes,
      ...notificationsRoutes,
      ...listingTrackingRoutes,
      ...listingLinksRoutes,
    ],
  },
  ...authRoutes,
  ...jumpRoutes,
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/features/errors/views/NotFound.vue'),
    meta: {
      title: 'Not Found | Pour Now',
    },
  },
]

if (import.meta.env.DEV) {
  // DEV-only preview route for the Retailers Breakdown Playwright screenshot/
  // alignment spec. Inserted before the NotFound catch-all; dead-code
  // eliminated from production builds.
  routes.splice(routes.length - 1, 0, {
    path: '/__rail-preview',
    name: 'RailPreview',
    component: () => import('@/views/RailPreview.vue'),
    meta: {
      title: 'Rail Preview | Pour Now',
    },
  })
}

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    const scrollElement = document.getElementById('scrollElement')
    if (scrollElement) {
      setTimeout(() => {
        scrollElement.scrollTo({ top: 0 })
      }, 500)
    }
  },
})

router.onError((error, to) => {
  if (error.message.includes('Failed to fetch dynamically imported module')) {
    window.location.href = to.fullPath
  }
})

router.beforeEach((to, from) => {
  if (from.path !== to.path) {
    posthog.capture('$pageleave')
  }

  const title = useTitle()

  const authStore = useAuthStore()
  const businessStore = useBusinessStore()
  const permissions = usePermissions()

  if (to.meta.title && typeof to.meta.title === 'string') {
    title.value = to.meta.title
  }

  if (to.name === 'NotFound') {
    return true
  }

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
  const isAuthenticated = !!authStore.token
  const toRoutePermissionIdentifiers = to.meta.permissionIdentifiers as PermissionType[]
  const toRouteVisibleEntityTypes = to.meta.visibleEntityTypes as AccountTypeId[]
  const entityTypeId = businessStore.getSelectedBusinessAccountTypeId as AccountTypeId
  const hasBusinessData = !!businessStore.businesses?.results.length

  if (requiresAuth && !isAuthenticated && to.name !== 'Jump') {
    return { name: 'Login' }
  }

  if (!requiresAuth && isAuthenticated && to.name !== 'Jump' && to.name !== 'InvitationAccept') {
    return { name: 'Admin' }
  }

  if (!hasBusinessData && to.name !== 'SignupBusiness' && requiresAuth) {
    return { name: 'SignupBusiness' }
  }

  const visibleToEntityType = toRouteVisibleEntityTypes ? toRouteVisibleEntityTypes.includes(entityTypeId) : true

  if (!visibleToEntityType) {
    return { name: from.name }
  }

  const hasPermissionToRoute = toRoutePermissionIdentifiers
    ? toRoutePermissionIdentifiers.some((identifier) => permissions.hasPermission(identifier))
    : true

  if (!hasPermissionToRoute) {
    return { name: from.name }
  }
})

router.afterEach(() => {
  posthog.capture('$pageview')
})

export default router
